import React, { useState, useEffect } from 'react';
import { userApi } from '../services/api';
import { useIsAdmin } from '../App';

interface User {
  username: string;
  email: string;
  name: string | null;
  status: string;
  enabled: boolean;
  createdAt: string;
}

const Settings: React.FC = () => {
  const isAdmin = useIsAdmin();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
  });
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userApi.getAll();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setFormData({ email: '', name: '' });
    setSuccessMessage(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      alert('Please enter an email address');
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage(null);
      
      await userApi.create({
        email: formData.email,
        name: formData.name || undefined,
      });
      
      setSuccessMessage(`User ${formData.email} created! A temporary password has been sent to their email.`);
      setFormData({ email: '', name: '' });
      loadUsers();
      
      // Keep modal open to show success message
      setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error creating user:', err);
      if (err.message?.includes('already exists')) {
        alert('A user with this email already exists.');
      } else {
        alert('Failed to create user. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (username: string, email: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}?`)) {
      return;
    }

    try {
      await userApi.delete(username);
      loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'status-confirmed';
      case 'FORCE_CHANGE_PASSWORD':
        return 'status-pending';
      default:
        return 'status-unknown';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'Active';
      case 'FORCE_CHANGE_PASSWORD':
        return 'Pending Password Reset';
      default:
        return status;
    }
  };

  if (!isAdmin) {
    return (
      <div className="settings-page">
        <div className="page-header"><h2>Users</h2></div>
        <p style={{ color: '#666', marginTop: '24px' }}>Access denied. Admin privileges required to manage users.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h2>User Management</h2>
          <p className="page-subtitle">Manage dashboard users and access</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={loadUsers}>
            🔄 Refresh
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            + Add User
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadUsers}>Try Again</button>
        </div>
      )}

      <div className="info-card">
        <h3>👥 How User Management Works</h3>
        <ul>
          <li>Add a new user by entering their email address</li>
          <li>They will receive a <strong>temporary password via email</strong></li>
          <li>On first login, they will be prompted to set a new password</li>
          <li>All users have full access to the dashboard</li>
        </ul>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-row">
                  <div className="empty-state">
                    <p>No users found.</p>
                    <button className="btn-primary" onClick={openAddModal}>
                      Add First User
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.username}>
                  <td>
                    <span className="user-email">{user.email}</span>
                  </td>
                  <td>{user.name || '-'}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                      {getStatusLabel(user.status)}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDeleteUser(user.username, user.email)}
                        title="Delete User"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            {successMessage ? (
              <div className="success-message">
                <span className="success-icon">✅</span>
                <p>{successMessage}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="user@example.com"
                  />
                  <small>A temporary password will be sent to this email</small>
                </div>
                <div className="form-group">
                  <label htmlFor="name">Name (Optional)</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="User's full name"
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
